import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowLeft,
	Building2,
	CheckCircle2,
	ClipboardCheck,
	Clock,
	Download,
	Eye,
	FileText,
	Loader2,
	Trash2,
	Upload,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";
import { VendorLayout } from "@/components/layouts/VendorLayout";
import { useBuyerContext, useVendorProfile } from "@/contexts/vendor-buyer-context";
import { PdfFormFillerV2 } from "@/components/pdf-filler";
import { DocumentUploadDialog } from "@/components/vendor/DocumentUploadDialog";
import type { FormFillerData } from "@/components/pdf-filler/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
export const Route = createFileRoute("/vendor/$buyerSlug/onboarding/$orgId")({
	component: VendorOnboardingDetailPage,
});

interface FormToFill {
	id: string;
	name: string;
	description: string;
	formTemplateId: string;
	existingResponses: Record<string, unknown>;
}

interface DocumentToUpload {
	id: string;
	name: string;
	description: string;
	isReplacing: boolean;
}

function VendorOnboardingDetailPage() {
	const { orgId, buyerSlug } = Route.useParams();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const vendorEmail = user?.email ?? "";
	const buyer = useBuyerContext();
	const vendorProfile = useVendorProfile();
	// Use the real email from Bidscope for signatures, fall back to portal email
	const signerEmail = vendorProfile.email || vendorEmail;

	// State for form filler modal
	const [formToFill, setFormToFill] = useState<FormToFill | null>(null);

	// State for document upload dialog
	const [documentToUpload, setDocumentToUpload] = useState<DocumentToUpload | null>(null);

	// Query organization info
	const { data: orgData, isLoading: orgLoading } = useQuery(
		trpc.public.organizations.get.queryOptions({
			organizationId: orgId,
		}),
	);

	// Query onboarding forms with inheritance
	const { data: formsData, isLoading: formsLoading } = useQuery(
		trpc.vendorPortal.onboarding.getFormsWithInheritance.queryOptions({
			organizationId: orgId,
		}),
	);

	// Query required documents with inheritance
	const { data: documentsData, isLoading: documentsLoading } = useQuery(
		trpc.vendorPortal.onboarding.getRequiredDocsWithInheritance.queryOptions({
			organizationId: orgId,
		}),
	);

	// Query compliance checks with inheritance
	const { data: complianceData, isLoading: complianceLoading } = useQuery(
		trpc.vendorPortal.onboarding.getComplianceChecksWithInheritance.queryOptions({
			organizationId: orgId,
		}),
	);

	// Query vendor's progress
	const { data: progressData, isLoading: progressLoading } = useQuery({
		...trpc.vendorPortal.onboarding.getVendorProgress.queryOptions({
			organizationId: orgId,
		}),
		enabled: !!user,
	});

	// Query existing form submissions for this vendor
	const { data: formSubmissionsData, isLoading: formSubmissionsLoading } = useQuery({
		...trpc.vendorPortal.formResponses.listByVendor.queryOptions({
			vendorEmail,
		}),
		enabled: !!vendorEmail,
	});

	// Query document submissions
	const { data: docSubmissionsData } = useQuery({
		...trpc.vendorPortal.onboarding.getDocumentSubmissions.queryOptions({
			organizationId: orgId,
		}),
		enabled: !!user,
	});

	// Query compliance acknowledgments
	const { data: complianceAcksData } = useQuery({
		...trpc.vendorPortal.onboarding.getComplianceAcknowledgments.queryOptions({
			organizationId: orgId,
		}),
		enabled: !!user,
	});

	// Helper to invalidate all vendor portal queries
	const invalidateVendorPortalQueries = () => {
		queryClient.invalidateQueries({
			predicate: (query) => {
				const key = query.queryKey[0];
				if (Array.isArray(key)) {
					return key[0] === "vendorPortal";
				}
				return false;
			},
		});
	};

	// Mutations
	const submitFormMutation = useMutation(
		trpc.vendorPortal.formResponses.submit.mutationOptions({
			onMutate: async (variables) => {
				// Clear any optimistic deletion for this form
				if (variables.orgFormId) {
					setOptimisticFormDeletions((prev) => {
						const newSet = new Set(prev);
						newSet.delete(variables.orgFormId!);
						return newSet;
					});
				}
				// Optimistically mark the form based on isDraft
				if (variables.orgFormId) {
					if (variables.isDraft) {
						// Mark as draft, remove from completions
						setOptimisticFormDrafts((prev) => new Set([...prev, variables.orgFormId!]));
						setOptimisticFormCompletions((prev) => {
							const newSet = new Set(prev);
							newSet.delete(variables.orgFormId!);
							return newSet;
						});
					} else {
						// Mark as completed, remove from drafts
						setOptimisticFormCompletions((prev) => new Set([...prev, variables.orgFormId!]));
						setOptimisticFormDrafts((prev) => {
							const newSet = new Set(prev);
							newSet.delete(variables.orgFormId!);
							return newSet;
						});
					}
				}
			},
			onSuccess: (_, variables) => {
				toast.success(variables.isDraft ? "Draft saved" : "Form submitted successfully");
				invalidateVendorPortalQueries();
			},
			onError: (error, variables) => {
				// Revert optimistic update on error
				if (variables.orgFormId) {
					if (variables.isDraft) {
						setOptimisticFormDrafts((prev) => {
							const newSet = new Set(prev);
							newSet.delete(variables.orgFormId!);
							return newSet;
						});
					} else {
						setOptimisticFormCompletions((prev) => {
							const newSet = new Set(prev);
							newSet.delete(variables.orgFormId!);
							return newSet;
						});
					}
				}
				toast.error(`Failed to ${variables.isDraft ? "save draft" : "submit form"}: ${error.message}`);
			},
		}),
	);

	// Track optimistic compliance states
	const [optimisticCompliance, setOptimisticCompliance] = useState<Record<string, boolean>>({});

	// Track optimistically uploaded document IDs
	const [optimisticDocUploads, setOptimisticDocUploads] = useState<Set<string>>(new Set());

	// Track optimistically completed form IDs
	const [optimisticFormCompletions, setOptimisticFormCompletions] = useState<Set<string>>(new Set());

	// Track optimistically deleted form IDs
	const [optimisticFormDeletions, setOptimisticFormDeletions] = useState<Set<string>>(new Set());

	// Track optimistically draft form IDs
	const [optimisticFormDrafts, setOptimisticFormDrafts] = useState<Set<string>>(new Set());

	// Clear optimistic document uploads once server data confirms them
	useEffect(() => {
		if (optimisticDocUploads.size > 0 && docSubmissionsData) {
			const serverUploadedIds = new Set(
				docSubmissionsData.map((ds) => ds.org_required_document_id)
			);
			// Clear optimistic entries that are now confirmed by server
			setOptimisticDocUploads((prev) => {
				const stillPending = new Set<string>();
				for (const id of prev) {
					if (!serverUploadedIds.has(id)) {
						stillPending.add(id);
					}
				}
				return stillPending.size === prev.size ? prev : stillPending;
			});
		}
	}, [docSubmissionsData, optimisticDocUploads.size]);

	// Clear optimistic form completions once server data confirms them
	useEffect(() => {
		if (optimisticFormCompletions.size > 0 && formSubmissionsData) {
			const serverCompletedIds = new Set(
				formSubmissionsData.filter((fs) => fs.org_form_id).map((fs) => fs.org_form_id)
			);
			setOptimisticFormCompletions((prev) => {
				const stillPending = new Set<string>();
				for (const id of prev) {
					if (!serverCompletedIds.has(id)) {
						stillPending.add(id);
					}
				}
				return stillPending.size === prev.size ? prev : stillPending;
			});
		}
	}, [formSubmissionsData, optimisticFormCompletions.size]);

	// Clear optimistic form deletions once server data confirms them
	useEffect(() => {
		if (optimisticFormDeletions.size > 0 && formSubmissionsData) {
			const serverFormIds = new Set(
				formSubmissionsData.filter((fs) => fs.org_form_id).map((fs) => fs.org_form_id)
			);
			setOptimisticFormDeletions((prev) => {
				const stillPending = new Set<string>();
				for (const id of prev) {
					// Keep optimistic deletion if server still shows the form
					if (serverFormIds.has(id)) {
						stillPending.add(id);
					}
				}
				return stillPending.size === prev.size ? prev : stillPending;
			});
		}
	}, [formSubmissionsData, optimisticFormDeletions.size]);

	// Clear optimistic form drafts once server data confirms them
	useEffect(() => {
		if (optimisticFormDrafts.size > 0 && formSubmissionsData) {
			const serverDraftIds = new Set(
				formSubmissionsData
					.filter((fs) => fs.org_form_id && fs.status === "draft")
					.map((fs) => fs.org_form_id)
			);
			setOptimisticFormDrafts((prev) => {
				const stillPending = new Set<string>();
				for (const id of prev) {
					if (!serverDraftIds.has(id)) {
						stillPending.add(id);
					}
				}
				return stillPending.size === prev.size ? prev : stillPending;
			});
		}
	}, [formSubmissionsData, optimisticFormDrafts.size]);

	const acknowledgeComplianceMutation = useMutation(
		trpc.vendorPortal.onboarding.acknowledgeComplianceCheck.mutationOptions({
			onMutate: async (variables) => {
				// Optimistically update the UI
				setOptimisticCompliance((prev) => ({
					...prev,
					[variables.orgComplianceCheckId]: variables.acknowledged,
				}));
			},
			onSuccess: () => {
				invalidateVendorPortalQueries();
			},
			onError: (error, variables) => {
				// Revert optimistic update on error
				setOptimisticCompliance((prev) => {
					const newState = { ...prev };
					delete newState[variables.orgComplianceCheckId];
					return newState;
				});
				toast.error(`Failed to update compliance: ${error.message}`);
			},
			// Note: We intentionally do NOT clear optimistic state in onSettled
			// because the query refetch triggered by onSuccess is async.
			// Clearing too early would cause a flash of stale data.
			// The optimistic state persists but is harmless - it matches the server state on success,
			// and onError handles the revert on failure.
		}),
	);

	// Mutation for generating filled PDF
	const generateFilledPdfMutation = useMutation(
		trpc.pdfTemplates.generateFilledPdfFromSubmission.mutationOptions(),
	);

	// Mutation for deleting form response
	const deleteFormResponseMutation = useMutation(
		trpc.vendorPortal.formResponses.delete.mutationOptions({
			onMutate: async (variables) => {
				// Optimistically mark the form as deleted
				if (variables.orgFormId) {
					setOptimisticFormCompletions((prev) => {
						const newSet = new Set(prev);
						newSet.delete(variables.orgFormId!);
						return newSet;
					});
					setOptimisticFormDeletions((prev) => new Set([...prev, variables.orgFormId!]));
				}
			},
			onSuccess: () => {
				toast.success("Form response deleted");
				invalidateVendorPortalQueries();
			},
			onError: (error, variables) => {
				// Revert optimistic deletion on error
				if (variables.orgFormId) {
					setOptimisticFormDeletions((prev) => {
						const newSet = new Set(prev);
						newSet.delete(variables.orgFormId!);
						return newSet;
					});
				}
				toast.error(`Failed to delete form response: ${error.message}`);
			},
		}),
	);

	// Loading state
	const isLoading = orgLoading || formsLoading || documentsLoading || complianceLoading || progressLoading || formSubmissionsLoading;

	if (isLoading) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</VendorLayout>
		);
	}

	if (!orgData) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="text-center py-12">
					<h2 className="text-2xl font-bold">Organization not found</h2>
					<p className="text-muted-foreground mt-2">
						The organization you're looking for doesn't exist.
					</p>
					<Button asChild className="mt-4">
						<Link to="/vendor/$buyerSlug/onboarding" params={{ buyerSlug }}>
							Back to Onboarding
						</Link>
					</Button>
				</div>
			</VendorLayout>
		);
	}

	// Map data for display - only "submitted" status counts as completed
	const submittedOrgFormIds = new Set(
		(formSubmissionsData ?? [])
			.filter((fs) => fs.org_form_id && fs.status === "submitted")
			.map((fs) => fs.org_form_id),
	);

	// Track draft form IDs
	const draftOrgFormIds = new Set(
		(formSubmissionsData ?? [])
			.filter((fs) => fs.org_form_id && fs.status === "draft")
			.map((fs) => fs.org_form_id),
	);

	const uploadedDocIds = new Set(
		(docSubmissionsData ?? []).map((ds) => ds.org_required_document_id),
	);

	const acknowledgedCheckIds = new Set(
		(complianceAcksData ?? [])
			.filter((ack) => ack.acknowledged)
			.map((ack) => ack.org_compliance_check_id),
	);

	const forms = (formsData ?? []).map((f) => {
		const template = f.form_templates as {
			id: string;
			pdf_path: string | null;
		} | null;
		const submission = (formSubmissionsData ?? []).find((fs) => fs.org_form_id === f.id);
		// Determine form status: "submitted", "draft", or null (not started)
		// Use optimistic state if available, otherwise use server state
		// Check optimistic deletions first
		let status: "submitted" | "draft" | null = null;
		if (optimisticFormDeletions.has(f.id)) {
			status = null; // Deleted optimistically
		} else if (optimisticFormCompletions.has(f.id) || submittedOrgFormIds.has(f.id)) {
			status = "submitted";
		} else if (optimisticFormDrafts.has(f.id) || draftOrgFormIds.has(f.id)) {
			status = "draft";
		}
		return {
			id: f.id,
			name: f.title,
			description: f.description ?? "",
			required: f.is_required,
			status,
			completed: status === "submitted",
			organizationName: f.organizationName,
			formTemplateId: template?.id ?? null,
			pdfPath: template?.pdf_path ?? null,
			submissionId: optimisticFormDeletions.has(f.id) ? null : (submission?.id ?? null),
		};
	});

	const documents = (documentsData ?? []).map((d) => {
		const submission = (docSubmissionsData ?? []).find((ds) => ds.org_required_document_id === d.id);
		// Use optimistic state if available, otherwise use server state
		const isUploaded = optimisticDocUploads.has(d.id) || uploadedDocIds.has(d.id);
		return {
			id: d.id,
			name: d.title,
			description: d.description ?? "",
			required: d.is_required,
			uploaded: isUploaded,
			organizationName: d.organizationName,
			filePath: submission?.file_path ?? null,
			originalFilename: submission?.original_filename ?? null,
		};
	});

	const compliance = (complianceData ?? []).map((c) => {
		// Use optimistic state if available, otherwise use server state
		const isAcknowledged = c.id in optimisticCompliance
			? optimisticCompliance[c.id]
			: acknowledgedCheckIds.has(c.id);
		return {
			id: c.id,
			name: c.title,
			description: c.description ?? "",
			required: c.is_required,
			acknowledged: isAcknowledged,
			organizationName: c.organizationName,
		};
	});

	const org = {
		id: orgData.id,
		name: orgData.name,
		type: orgData.type,
	};

	const handleFormComplete = async (form: typeof forms[0]) => {
		if (!vendorEmail) {
			toast.error("Please log in to complete forms");
			return;
		}

		if (!form.formTemplateId) {
			toast.error("Form template not found");
			return;
		}

		// Find existing submission for this form to pre-populate values
		const existingSubmission = (formSubmissionsData ?? []).find(
			(fs) => fs.org_form_id === form.id
		);
		const existingResponses = (existingSubmission?.field_responses as Record<string, unknown>) ?? {};

		// Set the form to fill with existing responses
		setFormToFill({
			id: form.id,
			name: form.name,
			description: form.description,
			formTemplateId: form.formTemplateId,
			existingResponses,
		});
	};

	const handleFormFillerClose = () => {
		setFormToFill(null);
	};

	const handleDeleteFormResponse = (formId: string) => {
		if (!vendorEmail) {
			toast.error("Please log in to delete form response");
			return;
		}
		deleteFormResponseMutation.mutate({
			orgFormId: formId,
			vendorEmail,
		});
	};

	const handleFormFillerSave = async (data: FormFillerData) => {
		if (!formToFill || !vendorEmail) return;

		await submitFormMutation.mutateAsync({
			orgFormId: formToFill.id,
			vendorEmail,
			fieldResponses: data.fieldResponses,
			isSigned: !!data.signatureData,
			signerName: data.signatureData?.signerName,
			signerEmail: data.signatureData?.signerEmail,
			signatureData: data.signatureData ?? null,
			isDraft: data.isDraft,
		});

		// Close the modal after successful save
		setFormToFill(null);
	};

	const handleComplianceToggle = (checkId: string, currentlyAcknowledged: boolean) => {
		acknowledgeComplianceMutation.mutate({
			orgComplianceCheckId: checkId,
			acknowledged: !currentlyAcknowledged,
		});
	};

	const handleDocumentUpload = (doc: typeof documents[0]) => {
		setDocumentToUpload({
			id: doc.id,
			name: doc.name,
			description: doc.description,
			isReplacing: doc.uploaded,
		});
	};

	const handleDocumentUploadClose = () => {
		setDocumentToUpload(null);
	};

	const handleDocumentUploadSuccess = (documentId: string) => {
		// Optimistically mark the document as uploaded
		setOptimisticDocUploads((prev) => new Set([...prev, documentId]));
	};

	// View handler for filled form PDF
	const handleViewFilledForm = async (submissionId: string | null) => {
		if (!submissionId) {
			toast.error("No submission found - please fill out the form first");
			return;
		}

		try {
			const result = await generateFilledPdfMutation.mutateAsync({
				submissionId,
			});

			if (result?.pdfBase64) {
				// Convert base64 to blob and open in new tab
				const byteCharacters = atob(result.pdfBase64);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: "application/pdf" });
				const url = URL.createObjectURL(blob);
				window.open(url, "_blank");
			} else {
				toast.error("Failed to generate filled PDF");
			}
		} catch (error) {
			console.error("View error:", error);
			toast.error("Failed to view filled form PDF");
		}
	};

	// Download handler for filled form PDF - generates PDF with field values applied
	const handleDownloadFilledForm = async (submissionId: string | null, title: string) => {
		if (!submissionId) {
			toast.error("No submission found - please fill out the form first");
			return;
		}

		try {
			// Use mutation to generate filled PDF on the fly
			const result = await generateFilledPdfMutation.mutateAsync({
				submissionId,
			});

			if (result?.pdfBase64) {
				// Convert base64 to blob and download
				const byteCharacters = atob(result.pdfBase64);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: "application/pdf" });

				// Create download link
				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = result.filename || `${title} - Filled.pdf`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			} else {
				toast.error("Failed to generate filled PDF");
			}
		} catch (error) {
			console.error("Download error:", error);
			toast.error("Failed to download filled form PDF");
		}
	};

	// Helper to check if file is PDF
	const isPdfFile = (filePath: string | null) => filePath?.toLowerCase().endsWith(".pdf");

	// View handler for uploaded documents (PDFs)
	const handleViewDocument = async (filePath: string | null) => {
		if (!filePath) {
			toast.error("No file available");
			return;
		}

		try {
			const result = await queryClient.fetchQuery(
				trpc.vendorPortal.onboarding.getDocumentDownloadUrl.queryOptions({ filePath })
			);
			if (result?.signedUrl) {
				window.open(result.signedUrl, "_blank");
			} else {
				toast.error("Failed to get file URL");
			}
		} catch (error) {
			toast.error("Failed to view document");
		}
	};

	// Download handler for uploaded documents
	const handleDownloadDocument = async (filePath: string | null, title: string) => {
		if (!filePath) {
			toast.error("No file available for download");
			return;
		}

		try {
			const result = await queryClient.fetchQuery(
				trpc.vendorPortal.onboarding.getDocumentDownloadUrl.queryOptions({ filePath })
			);
			if (result?.signedUrl) {
				// Fetch and download the file
				const response = await fetch(result.signedUrl);
				const blob = await response.blob();
				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = title || "document";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			} else {
				toast.error("Failed to get download URL");
			}
		} catch (error) {
			toast.error("Failed to download document");
		}
	};

	// Calculate progress
	const totalRequired =
		forms.filter((f) => f.required).length +
		documents.filter((d) => d.required).length +
		compliance.filter((c) => c.required).length;
	const totalCompleted =
		forms.filter((f) => f.required && f.completed).length +
		documents.filter((d) => d.required && d.uploaded).length +
		compliance.filter((c) => c.required && c.acknowledged).length;
	const progress = totalRequired > 0 ? Math.round((totalCompleted / totalRequired) * 100) : 100;

	const hasNoRequirements = forms.length === 0 && documents.length === 0 && compliance.length === 0;

	if (hasNoRequirements) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="space-y-6">
					<div className="flex items-center gap-2">
						<Button variant="ghost" size="icon" asChild>
							<Link to="/vendor/$buyerSlug/onboarding" params={{ buyerSlug }}>
								<ArrowLeft className="h-4 w-4" />
							</Link>
						</Button>
						<h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
							<Building2 className="h-6 w-6" />
							{org.name}
						</h1>
					</div>
					<Card>
						<CardContent className="py-12 text-center">
							<CheckCircle2 className="h-16 w-16 mx-auto text-green-500 mb-4" />
							<h2 className="text-xl font-semibold mb-2">No Onboarding Requirements</h2>
							<p className="text-muted-foreground">
								This organization has no onboarding requirements configured.
								You're ready to submit bids!
							</p>
						</CardContent>
					</Card>
				</div>
			</VendorLayout>
		);
	}

	return (
		<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					<div className="space-y-1">
						<div className="flex items-center gap-2">
							<Button variant="ghost" size="icon" asChild>
								<Link to="/vendor/$buyerSlug/onboarding" params={{ buyerSlug }}>
									<ArrowLeft className="h-4 w-4" />
								</Link>
							</Button>
							<Badge variant="outline" className="capitalize">
								{org.type}
							</Badge>
						</div>
						<h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
							<Building2 className="h-6 w-6" />
							{org.name}
						</h1>
						<p className="text-muted-foreground">
							Complete the onboarding requirements to become an approved vendor
						</p>
					</div>
				</div>

				{/* Progress */}
				<Card>
					<CardContent className="pt-6">
						<div className="flex items-center justify-between mb-2">
							<span className="font-medium">Onboarding Progress</span>
							<span className="text-sm text-muted-foreground">
								{progress}% complete
							</span>
						</div>
						<Progress value={progress} className="h-3" />
						<div className="flex items-center justify-between mt-4 text-sm">
							<div className="flex items-center gap-4">
								<span className="flex items-center gap-1">
									<FileText className="h-4 w-4 text-muted-foreground" />
									Forms: {forms.filter((f) => f.completed).length}/{forms.length}
								</span>
								<span className="flex items-center gap-1">
									<Upload className="h-4 w-4 text-muted-foreground" />
									Docs: {documents.filter((d) => d.uploaded).length}/{documents.length}
								</span>
								<span className="flex items-center gap-1">
									<ClipboardCheck className="h-4 w-4 text-muted-foreground" />
									Compliance: {compliance.filter((c) => c.acknowledged).length}/{compliance.length}
								</span>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Forms */}
				{forms.length > 0 && (
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<FileText className="h-5 w-5" />
								Required Forms
							</CardTitle>
							<CardDescription>
								Complete all required forms for this organization
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-3">
							{forms.map((form) => (
								<div
									key={form.id}
									className={`flex items-start justify-between p-4 border rounded-lg ${
										form.status === "submitted"
											? "bg-green-50 border-green-200"
											: form.status === "draft"
											? "bg-amber-50 border-amber-200"
											: ""
									}`}
								>
									<div className="flex items-start gap-3">
										{form.status === "submitted" ? (
											<CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
										) : form.status === "draft" ? (
											<Clock className="h-5 w-5 text-amber-600 mt-0.5" />
										) : (
											<FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
										)}
										<div>
											<div className="flex items-center gap-2">
												<span className="font-medium">{form.name}</span>
												<Badge variant={form.required ? "default" : "outline"}>
													{form.required ? "Required" : "Optional"}
												</Badge>
												{form.status === "draft" && (
													<Badge variant="outline" className="text-amber-600 border-amber-300">
														Draft
													</Badge>
												)}
											</div>
											{form.description && (
												<p className="text-sm text-muted-foreground mt-1">
													{form.description}
												</p>
											)}
											<p className="text-xs text-muted-foreground mt-1">
												From: {form.organizationName}
											</p>
										</div>
									</div>
									<div className="flex items-center gap-2">
										{/* View/Download filled form (only show when form is completed/submitted) */}
										{form.status === "submitted" && form.submissionId && (
											<>
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="sm"
															onClick={() => handleViewFilledForm(form.submissionId)}
														>
															<Eye className="h-4 w-4 text-green-600" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>View form</TooltipContent>
												</Tooltip>
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="sm"
															onClick={() => handleDownloadFilledForm(form.submissionId, form.name)}
														>
															<Download className="h-4 w-4 text-green-600" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>Download form</TooltipContent>
												</Tooltip>
											</>
										)}
										{/* Delete button (only show when form has a submission) */}
										{form.status && (
											<Tooltip>
												<TooltipTrigger asChild>
													<Button
														variant="ghost"
														size="sm"
														onClick={() => handleDeleteFormResponse(form.id)}
														disabled={deleteFormResponseMutation.isPending}
													>
														<Trash2 className="h-4 w-4 text-red-500" />
													</Button>
												</TooltipTrigger>
												<TooltipContent>Delete response</TooltipContent>
											</Tooltip>
										)}
										<Button
											variant={form.status ? "outline" : "default"}
											size="sm"
											onClick={() => handleFormComplete(form)}
											disabled={submitFormMutation.isPending || formSubmissionsLoading}
										>
											{form.status === "submitted"
												? "Edit"
												: form.status === "draft"
												? "Continue"
												: "Fill Out"}
										</Button>
									</div>
								</div>
							))}
						</CardContent>
					</Card>
				)}

				{/* Documents */}
				{documents.length > 0 && (
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Upload className="h-5 w-5" />
								Required Documents
							</CardTitle>
							<CardDescription>
								Upload all required documents for this organization
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-3">
							{documents.map((doc) => (
								<div
									key={doc.id}
									className={`flex items-start justify-between p-4 border rounded-lg ${
										doc.uploaded ? "bg-green-50 border-green-200" : ""
									}`}
								>
									<div className="flex items-start gap-3">
										{doc.uploaded ? (
											<CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
										) : (
											<Upload className="h-5 w-5 text-muted-foreground mt-0.5" />
										)}
										<div>
											<div className="flex items-center gap-2">
												<span className="font-medium">{doc.name}</span>
												<Badge variant={doc.required ? "default" : "outline"}>
													{doc.required ? "Required" : "Optional"}
												</Badge>
											</div>
											{doc.description && (
												<p className="text-sm text-muted-foreground mt-1">
													{doc.description}
												</p>
											)}
											<p className="text-xs text-muted-foreground mt-1">
												From: {doc.organizationName}
											</p>
										</div>
									</div>
									<div className="flex items-center gap-2">
										{/* View/Download uploaded document - hide while optimistic update is pending to avoid stale filePath */}
										{doc.uploaded && doc.filePath && !optimisticDocUploads.has(doc.id) && (
											<>
												{isPdfFile(doc.filePath) && (
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="sm"
																onClick={() => handleViewDocument(doc.filePath)}
															>
																<Eye className="h-4 w-4 text-green-600" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>View PDF</TooltipContent>
													</Tooltip>
												)}
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="sm"
															onClick={() => handleDownloadDocument(doc.filePath, doc.name)}
														>
															<Download className="h-4 w-4 text-green-600" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>
														Download {doc.originalFilename || doc.name}
													</TooltipContent>
												</Tooltip>
											</>
										)}
										<Button
											variant={doc.uploaded ? "outline" : "default"}
											size="sm"
											onClick={() => handleDocumentUpload(doc)}
										>
											{doc.uploaded ? "Replace" : "Upload"}
										</Button>
									</div>
								</div>
							))}
						</CardContent>
					</Card>
				)}

				{/* Compliance */}
				{compliance.length > 0 && (
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<ClipboardCheck className="h-5 w-5" />
								Compliance Acknowledgments
							</CardTitle>
							<CardDescription>
								Confirm that you meet all compliance requirements
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-3">
							{compliance.map((comp) => (
								<div
									key={comp.id}
									className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
										comp.acknowledged
											? "bg-green-50 border-green-200"
											: "hover:bg-muted/50"
									}`}
									onClick={() => handleComplianceToggle(comp.id, comp.acknowledged)}
								>
									<Checkbox
										checked={comp.acknowledged}
										disabled={acknowledgeComplianceMutation.isPending}
										onCheckedChange={() => handleComplianceToggle(comp.id, comp.acknowledged)}
									/>
									<div className="flex-1">
										<div className="flex items-center gap-2">
											<span className="font-medium">{comp.name}</span>
											<Badge variant={comp.required ? "default" : "outline"}>
												{comp.required ? "Required" : "Optional"}
											</Badge>
										</div>
										{comp.description && (
											<p className="text-sm text-muted-foreground mt-1">
												{comp.description}
											</p>
										)}
										<p className="text-xs text-muted-foreground mt-1">
											From: {comp.organizationName}
										</p>
									</div>
								</div>
							))}
						</CardContent>
					</Card>
				)}

			</div>

			{/* Form Filler Modal */}
			<PdfFormFillerV2
				open={!!formToFill}
				onClose={handleFormFillerClose}
				onSave={handleFormFillerSave}
				formName={formToFill?.name ?? ""}
				formDescription={formToFill?.description}
				formTemplateId={formToFill?.formTemplateId ?? ""}
				existingResponses={formToFill?.existingResponses ?? {}}
				requireSignature={true}
				signerName={`${vendorProfile.first_name} ${vendorProfile.last_name}`}
				signerEmail={signerEmail}
			/>

			{/* Document Upload Dialog */}
			<DocumentUploadDialog
				open={!!documentToUpload}
				onClose={handleDocumentUploadClose}
				onSuccess={handleDocumentUploadSuccess}
				documentId={documentToUpload?.id ?? ""}
				documentName={documentToUpload?.name ?? ""}
				documentDescription={documentToUpload?.description}
				isReplacing={documentToUpload?.isReplacing}
			/>
		</VendorLayout>
	);
}
